import { API, Auth } from 'aws-amplify';

const FAILURE_COOLDOWN_MS = 5000;

let portalUserRefreshPromise = null;
const recentFailures = new Map();

/** One shared Cognito refresh per page load (dedupes App, AdminRoute, etc.). */
export function getPortalUser({ refresh = false } = {}) {
  if (refresh) {
    if (!portalUserRefreshPromise) {
      portalUserRefreshPromise = Auth.currentAuthenticatedUser({ bypassCache: true }).finally(
        () => {
          portalUserRefreshPromise = null;
        },
      );
    }
    return portalUserRefreshPromise;
  }
  return Auth.currentAuthenticatedUser();
}

export async function getPortalAccessJwt() {
  try {
    const session = await Auth.currentSession();
    return session.getAccessToken().getJwtToken();
  } catch (sessionErr) {
    const user = await getPortalUser();
    const token = user?.signInUserSession?.accessToken?.getJwtToken();
    if (!token) throw sessionErr;
    return token;
  }
}

function requestKey(params) {
  const copy = { ...(params || {}) };
  delete copy.jwt;
  delete copy.back_end;
  return JSON.stringify(Object.keys(copy).sort().map((key) => [key, copy[key]]));
}

function cachedFailure(key) {
  const recent = recentFailures.get(key);
  if (!recent) return null;
  if (recent.until <= Date.now()) {
    recentFailures.delete(key);
    return null;
  }
  return recent.error;
}

export async function callAgoGet(queryStringParameters) {
  const key = requestKey(queryStringParameters);
  const recentError = cachedFailure(key);
  if (recentError) throw recentError;
  try {
    const jwt = await getPortalAccessJwt();
    const res = await API.get('adminWebTools', '/ago', {
      queryStringParameters: {
        back_end: process.env.REACT_APP_BACKEND,
        jwt,
        ...queryStringParameters,
      },
    });
    recentFailures.delete(key);
    return res;
  } catch (err) {
    recentFailures.set(key, { until: Date.now() + FAILURE_COOLDOWN_MS, error: err });
    throw err;
  }
}

let portalSessionPromise = null;
let portalSessionToken = 0;
let portalSessionFailure = null;
let portalSessionFailedUntil = 0;

const SESSION_QUERY = {
  api_endpoint: 'api3:portal:session',
  request_type: 'GET',
};

function rememberSessionFailure(token, err) {
  if (portalSessionToken !== token) return;
  portalSessionPromise = null;
  portalSessionFailure = err;
  portalSessionFailedUntil = Date.now() + FAILURE_COOLDOWN_MS;
}

/** One shell read per page load. A failure stays cached for a few seconds. signIn passes refresh. */
export function getPortalSession({ refresh = false } = {}) {
  if (refresh) {
    portalSessionPromise = null;
    portalSessionFailure = null;
    portalSessionFailedUntil = 0;
    recentFailures.delete(requestKey(SESSION_QUERY));
  }
  if (!refresh && portalSessionFailure && Date.now() < portalSessionFailedUntil) {
    return Promise.reject(portalSessionFailure);
  }
  if (!portalSessionPromise) {
    const token = ++portalSessionToken;
    portalSessionPromise = callAgoGet(SESSION_QUERY)
      .then((res) => {
        if (!res || res.status === 'error') {
          const err = new Error((res && res.message) || 'portal session failed');
          rememberSessionFailure(token, err);
          throw err;
        }
        if (portalSessionToken === token) {
          portalSessionFailure = null;
          portalSessionFailedUntil = 0;
        }
        return res;
      })
      .catch((err) => {
        rememberSessionFailure(token, err);
        throw err;
      });
  }
  return portalSessionPromise;
}

/** One attempt. A failure is cooled down inside callAgoGet so a remount does not hit the network again. */
export async function callAgoGetWithRetry(queryStringParameters) {
  return callAgoGet(queryStringParameters);
}
